import React, { useEffect, useState } from 'react'
import { Card, Form, Button, Row, Col, Spin, Table, Space } from 'antd'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import BaseListing from 'src/BaseListings'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import { STATUS_NAME, VOUCHER_STATUS_DROPDOWN_OPTIONS } from 'src/models/vouchers'
import WidgetProjectSelector from 'src/widgets/Selectors/WidgetProjectSelector'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatMoney, wtFormatRoundedValue } from 'src/helpers/commons/utils'
import { wtSubYearsToDate } from 'src/helpers/commons/moment'
import WidgetDatePicker from 'src/widgets/DatePickers/WidgetDatePicker'
import { TYPE_GROUP, TYPE_TOTAL } from 'src/models/pnlConfig'
import { CaretDownFilled, CaretRightFilled, FilePdfOutlined } from '@ant-design/icons'
import Pdf from './Pdf'

const isRowVisible = (row) => {
  const hasIsDisplay = Object.prototype.hasOwnProperty.call(row, 'is_display')
  const hasIsShow = Object.prototype.hasOwnProperty.call(row, 'is_show')

  const isDisplayVisible = !hasIsDisplay || row.is_display === true || row.is_display === 1 || row.is_display === '1'
  const isShowVisible = !hasIsShow || row.is_show === true || row.is_show === 1 || row.is_show === '1'

  return isDisplayVisible && isShowVisible
}

const normalizeRows = (rows = []) => {
  return rows
    .filter((row) => isRowVisible(row))
    .map((row) => {
      const children = Array.isArray(row.children) ? normalizeRows(row.children) : []

      return {
        ...row,
        name: row.label && String(row.label).trim() !== '' ? row.label : row.name,
        children,
        hasItems: children.length > 0,
      }
    })
}

const isPercentageRow = (row) => row?.is_percentage === true || row?.is_percentage === 1 || row?.is_percentage === '1'

const isLabelRow = (row) => row?.is_label === true || row?.is_label === 1 || row?.is_label === '1'

const formatValue = (value, row) => {
  if (isLabelRow(row)) {
    return ''
  }

  if (isPercentageRow(row)) {
    return `${Number(value || 0).toFixed(2)} %`
  }

  return wtFormatMoney(value, '', true, 2)
}

const RatioAnalysisV1 = () => {
  const Base = new BaseListing('ratio_analysis_v1')
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()

  const [projects, setProjects] = useState([])
  const branches = wtGetFromLocalStorage('branches')

  const [projectId, setProjectId] = useState()
  const [projectName, setProjectName] = useState('')
  const [voucherStatus, setVoucherStatus] = useState()
  const [voucherStatusName, setVoucherStatusName] = useState('')
  const [branchId, setBranchId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [ratioAnalysisData, setRatioAnalysisData] = useState([])
  const [asOf, setAsOf] = useState('')
  const [boldRows, setBoldRows] = useState([])
  const [expandedRows, setExpandedRows] = useState([])

  const lastYearLastDate = asOf ? wtSubYearsToDate(asOf, 1, 'YYYY-MM-DD') : ''
  const companyName = wtGetFromLocalStorage('company_name')

  const getLink = (record, text, key) => {
    let toDate = ''
    if (key === 'asof') {
      toDate = asOf
    }
    if (key === 'last') {
      toDate = lastYearLastDate
    }

    if (record.type !== TYPE_TOTAL && record.source_id) {
      const queryParams = `fromDate=2000-01-01&toDate=${toDate}&accountId=${+record.source_id}&voucherStatus=${+voucherStatus}&branchId=${+branchId}&branchName=${branchName}&projectId=${+projectId} `
      return (
        <a
          href={'/reports?' + queryParams + '#code:account-statement'}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: '#000000' }}
        >
          {formatValue(text, record)}
        </a>
      )
    }

    return formatValue(text, record)
  }

  const ratioAnalysisHeaderColumns = [
    {
      title: <span style={{ fontSize: '0.8rem', marginLeft: '5%' }}>Account Description</span>,
      dataIndex: 'name',
      key: 'name',
      align: 'left',
      width: '70%',
      onCell: (record) => ({
        onClick: () => handleExpand(record),
        style: { cursor: 'pointer' },
      }),
      render: (text, record) => (
        <span
          style={{
            fontWeight: record.type === TYPE_TOTAL || boldRows.includes(record.key) ? 'bold' : 'normal',
            fontSize: record.type === TYPE_TOTAL ? '0.83rem' : '0.8rem',
            cursor: 'pointer',
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: <span style={{ fontSize: '0.8rem' }}>{'As Of ' + asOf}</span>,
      align: 'right',
      children: [
        {
          title: <span style={{ fontSize: '0.8rem' }}>Balance ( .Rs. )</span>,
          dataIndex: 'current_year_balance',
          key: 'current_year_balance',
          align: 'right',
          render: (text, record) => (
            <span
              style={{
                fontWeight: record.type === TYPE_TOTAL || boldRows.includes(record.key) ? 'bold' : 'normal',
                fontSize: record.type === TYPE_TOTAL ? '0.83rem' : '0.8rem',
              }}
            >
              {getLink(record, text, 'asof')}
            </span>
          ),
        },
      ],
    },
    {
      title: <span style={{ fontSize: '0.8rem' }}>{'As Of ' + lastYearLastDate}</span>,
      align: 'right',
      children: [
        {
          title: <span style={{ fontSize: '0.8rem' }}>Balance ( .Rs. )</span>,
          dataIndex: 'last_year_balance',
          key: 'last_year_balance',
          align: 'right',
          render: (text, record) => (
            <span
              style={{
                fontWeight: record.type === TYPE_TOTAL || boldRows.includes(record.key) ? 'bold' : 'normal',
                fontSize: record.type === TYPE_TOTAL ? '0.83rem' : '0.8rem',
              }}
            >
              {getLink(record, text, 'last')}
            </span>
          ),
        },
      ],
    },
    {
      title: <span style={{ fontSize: '0.8rem', marginLeft: '5%' }}>%Age</span>,
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'right',
      render: (text, record) => (
        <span
          style={{
            fontWeight: record.type === TYPE_TOTAL || boldRows.includes(record.key) ? 'bold' : 'normal',
            fontSize: record.type === TYPE_TOTAL ? '0.83rem' : '0.8rem',
          }}
        >
          {isLabelRow(record) || isPercentageRow(record) ? '' : wtFormatRoundedValue(text, '', true, 2)}
        </span>
      ),
    },
  ]

  const changeValues = (key, val) => {
    setRatioAnalysisData([])

    if (key === 'branch_id') {
      setBranchId(val.value)
      setBranchName(val.label)
    }
    if (key === 'project_id') {
      setProjectId(val)
      if (val === '') {
        setProjectName('')
      }
    }
    if (key === 'voucher_status') {
      setVoucherStatus(val.value)
      setVoucherStatusName(val.label)
    }
    if (key === 'as_of') {
      setAsOf(val)
    }
  }

  const getQueryParams = () => {
    const url = new URL(window.location.href)
    const searchParams = new URLSearchParams(url.search)

    const queryAsOf = searchParams.get('asOf')
    const queryVoucherStatus = searchParams.get('voucherStatus')
    const queryBranchId = searchParams.get('branchId')
    const queryBranchName = searchParams.get('branchName')
    const queryProjectId = searchParams.get('projectId')

    if (queryAsOf !== '' && typeof queryAsOf !== 'undefined' && queryAsOf !== null) {
      setAsOf(queryAsOf)
    }
    if (+queryVoucherStatus > 0) {
      setVoucherStatus(queryVoucherStatus)
      setVoucherStatusName(STATUS_NAME[queryVoucherStatus])
    }
    if (+queryBranchId > 0) {
      setBranchId(queryBranchId)
      setBranchName(queryBranchName)
    }
    if (+queryProjectId > 0) {
      setProjectId(+queryProjectId)
    }
  }

  const getReportData = () => {
    setIsLoading(true)
    wtApiCall(
      'reports/ratio_analysis_v1',
      {
        as_of: asOf,
        last_year: lastYearLastDate,
        branch_id: branchId,
        project_id: projectId,
        voucher_status: voucherStatus,
        version: 2,
      },
      'get',
      (response) => {
        if (response !== null && response.data !== null) {
          if (response.data.hasOwnProperty('ratio_analysis_v1_data')) {
            setRatioAnalysisData(normalizeRows(response.data.ratio_analysis_v1_data || []))
          } else {
            setRatioAnalysisData([])
          }
        }
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const expandIcon = ({ expanded, onExpand, record }) => {
    if (record.hasItems && record.type === TYPE_GROUP) {
      return (
        <span style={{ marginRight: '2%' }}>
          {expanded ? (
            <CaretDownFilled style={{ color: '#706f71', fontSize: 12 }} onClick={(e) => onExpand(record, e)} />
          ) : (
            <CaretRightFilled style={{ color: '#706f71', fontSize: 12 }} onClick={(e) => onExpand(record, e)} />
          )}
        </span>
      )
    }

    return <span style={{ marginRight: '3%' }}></span>
  }

  const handleExpand = (record) => {
    const key = record.key
    if (record.type === TYPE_GROUP && record.hasItems) {
      const isExpanded = expandedRows.includes(key)
      if (isExpanded) {
        setExpandedRows(expandedRows.filter((rowKey) => rowKey !== key))
        setBoldRows((prevBoldRows) => prevBoldRows.filter((rowKey) => rowKey !== record.key))
      } else {
        setExpandedRows([...expandedRows, key])
        setBoldRows((prevBoldRows) => [...prevBoldRows, record.key])
      }
    }
  }

  const exportToPdf = async (includeExecutiveSummary = true) => {
    setIsLoading(true)
    try {
      await Pdf(ratioAnalysisData, asOf, lastYearLastDate, { includeExecutiveSummary })
    } catch (error) {
      console.error('Failed to generate PDF', error)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    Base.dropdownData('projects', {}, [setProjects], 1)
    getQueryParams()
  }, [])

  return (
    <div className="container-fluid" style={{ padding: '1em', backgroundColor: 'white', minHeight: '100vh', fontFamily: 'Helvetica' }}>
      <Form layout="vertical" form={form} onFinish={getReportData} style={{ maxWidth: '100%', margin: '0 auto' }}>
        <Card title={<span style={{ fontSize: '22px', marginLeft: '40%' }}>{companyName + ' Ratio Analysis V1'}</span>} style={{ width: '100%' }}>
          <Row gutter={[16, 16]}>
            <Col xs={4} sm={2} md={1}></Col>
            <Col xs={15} sm={10} md={4}>
              <label>As Of: *</label>
              <WidgetDatePicker value={asOf} setDate={setAsOf} placeholder="As Of" />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <label>Voucher Status:</label>
              <WidgetSelector
                selectedValue={voucherStatus}
                displayValue={voucherStatusName}
                options={VOUCHER_STATUS_DROPDOWN_OPTIONS}
                showCloudIcon={false}
                size="md"
                onChange={(values) => changeValues('voucher_status', values)}
              />
            </Col>
            <Col xs={18} sm={12} md={6}>
              <label>Branch:</label>
              <WidgetBranchSelector
                value={branchId}
                displayValue={branchName}
                branches={branches}
                placeholder="Select Branch"
                onChange={(values) => changeValues('branch_id', values)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={18} sm={12} md={6}>
              <label>Project:</label>
              <WidgetProjectSelector
                onChange={(value) => changeValues('project_id', value)}
                data={projects}
                value={projectId}
                displayValue={projectName}
                height={35}
                reloadDropdownData={() => Base.dropdownData('projects', {}, [setProjects], 1)}
              />
            </Col>
            <Col xs={8} sm={4} md={2}>
              <Button style={{ marginTop: 35 }} type="primary" htmlType="submit" disabled={isLoading}>
                Search
              </Button>
            </Col>
          </Row>
        </Card>

        <br />
        <br />
        <div style={{ overflow: 'auto', margin: 'auto', width: '80%' }}>
          <Space style={{ marginBottom: 10, float: 'right' }}>
            <Button
              style={{ backgroundColor: '#FF0000', color: '#fff' }}
              onClick={() => exportToPdf(true)}
              disabled={isLoading || ratioAnalysisData.length <= 0}
              icon={<FilePdfOutlined />}
            >
              With Summary
            </Button>
            <Button
              style={{ backgroundColor: '#B91C1C', color: '#fff' }}
              onClick={() => exportToPdf(false)}
              disabled={isLoading || ratioAnalysisData.length <= 0}
              icon={<FilePdfOutlined />}
            >
              Without Summary
            </Button>
          </Space>
          <Table
            columns={ratioAnalysisHeaderColumns}
            dataSource={ratioAnalysisData}
            pagination={false}
            expandable={{ expandedRowKeys: expandedRows, expandIcon: expandIcon }}
            scroll={{ x: 'max-content' }}
            bordered
            size="small"
            loading={{ indicator: <div><Spin /></div>, spinning: isLoading }}
          />
        </div>
      </Form>
    </div>
  )
}

export default RatioAnalysisV1
